import { cellClassName, cellStyle } from '@/components/ui/shared/Table/Cell';
import css from '@/components/ui/shared/Table/Table.module.css';
import { useTableContext } from '@/components/ui/shared/Table/TableContext';
import type { CellAlign, HideBelow } from '@/components/ui/shared/Table/types';
import SimpleArrowDown from '@/styles/Icons/SimpleArrowDown';
import { classNames } from '@/utils/helpers/classNames';
import type { ReactNode } from 'react';

export interface HeaderCellProps {
  /** Presence makes the header a sort trigger for this key. */
  sortKey?: string;
  width?: string;
  align?: CellAlign;
  hideBelow?: HideBelow;
  className?: string;
  children: ReactNode;
}

export function HeaderCell({
  sortKey,
  width,
  align,
  hideBelow,
  className,
  children
}: HeaderCellProps) {
  const { sort, onSortChange } = useTableContext();
  const isActive = sortKey != null && sort?.key === sortKey;
  const ariaSort = isActive
    ? sort?.direction === 'asc'
      ? 'ascending'
      : 'descending'
    : undefined;

  const handleClick = () => {
    if (!sortKey || !onSortChange) return;
    const direction =
      isActive && sort?.direction === 'asc' ? 'desc' : 'asc';
    onSortChange({ key: sortKey, direction });
  };

  return (
    <div
      role="columnheader"
      aria-sort={ariaSort}
      className={cellClassName(css.headerCell, {
        width,
        align,
        hideBelow,
        className
      })}
      style={cellStyle(width)}
    >
      {sortKey && onSortChange ? (
        <button type="button" className={css.sortButton} onClick={handleClick}>
          {children}
          <SimpleArrowDown
            className={classNames({
              [css.sortIcon]: true,
              [css.sortAsc]: isActive && sort?.direction === 'asc',
              [css.sortHidden]: !isActive
            })}
          />
        </button>
      ) : (
        children
      )}
    </div>
  );
}
